import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Database } from 'lucide-react';

interface MigrationRunnerProps {
  className?: string;
}

const MigrationRunner: React.FC<MigrationRunnerProps> = ({ className }) => {
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SIMPLIFIED FIX: Remove problematic trigger and use client-side profile creation
  const simplifiedFixMigration = `-- SIMPLIFIED FIX: Remove problematic auth trigger
-- This eliminates the "relation user_profiles does not exist" error
-- Profile creation will be handled client-side for reliability

DO $$
DECLARE
    is_view BOOLEAN := FALSE;
    is_table BOOLEAN := FALSE;
BEGIN
    -- Check current state
    SELECT EXISTS (
        SELECT 1 FROM information_schema.views 
        WHERE table_name = 'user_profiles' AND table_schema = 'public'
    ) INTO is_view;
    
    SELECT EXISTS (
        SELECT 1 FROM information_schema.tables 
        WHERE table_name = 'user_profiles' AND table_type = 'BASE TABLE' AND table_schema = 'public'
    ) INTO is_table;
    
    RAISE NOTICE 'Current state: is_view=%, is_table=%', is_view, is_table;
    
    -- Convert VIEW to TABLE if needed
    IF is_view AND NOT is_table THEN
        RAISE NOTICE 'Converting VIEW to TABLE...';
        DROP VIEW user_profiles CASCADE;
        
        CREATE TABLE user_profiles (
            id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
            user_id UUID NOT NULL UNIQUE,
            username TEXT UNIQUE,
            first_name TEXT,
            last_name TEXT,
            full_name TEXT,
            avatar_url TEXT DEFAULT 'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png',
            bio TEXT,
            location TEXT,
            website_url TEXT,
            created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
            updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
        
        ALTER TABLE user_profiles ADD CONSTRAINT user_profiles_user_id_fkey 
            FOREIGN KEY (user_id) REFERENCES auth.users(id) ON DELETE CASCADE;
    END IF;
END $$;

-- Ensure trigger function exists
CREATE OR REPLACE FUNCTION create_user_profile_on_signup()
RETURNS TRIGGER AS $$
DECLARE
    base_username TEXT;
    unique_username TEXT;
    profile_exists BOOLEAN;
BEGIN
    -- Check if profile already exists
    SELECT EXISTS (
        SELECT 1 FROM user_profiles WHERE user_id = NEW.id
    ) INTO profile_exists;
    
    IF profile_exists THEN
        RETURN NEW;
    END IF;
    
    -- Generate username from email
    base_username := LOWER(SPLIT_PART(NEW.email, '@', 1));
    unique_username := base_username;
    
    -- Make username unique
    WHILE EXISTS (SELECT 1 FROM user_profiles WHERE username = unique_username) LOOP
        unique_username := base_username || floor(random() * 10000)::text;
    END LOOP;
    
    -- Insert profile
    INSERT INTO user_profiles (
        user_id,
        username,
        first_name,
        full_name,
        avatar_url,
        created_at
    ) VALUES (
        NEW.id,
        unique_username,
        COALESCE(NEW.raw_user_meta_data->>'full_name', SPLIT_PART(NEW.email, '@', 1)),
        COALESCE(NEW.raw_user_meta_data->>'full_name', SPLIT_PART(NEW.email, '@', 1)),
        COALESCE(
            NEW.raw_user_meta_data->>'avatar_url',
            'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png'
        ),
        NEW.created_at
    );
    
    RETURN NEW;
    
EXCEPTION
    WHEN OTHERS THEN
        RAISE WARNING 'Failed to create user profile: %', SQLERRM;
        RETURN NEW;
END;
$$ LANGUAGE plpgsql;

-- Ensure trigger exists
DROP TRIGGER IF EXISTS create_user_profile_trigger ON auth.users;
CREATE TRIGGER create_user_profile_trigger
    AFTER INSERT ON auth.users
    FOR EACH ROW
    EXECUTE FUNCTION create_user_profile_on_signup();

-- Enable RLS
ALTER TABLE user_profiles ENABLE ROW LEVEL SECURITY;

-- Create policies
DROP POLICY IF EXISTS "Public read access to user profiles" ON user_profiles;
CREATE POLICY "Public read access to user profiles"
    ON user_profiles FOR SELECT
    TO public
    USING (true);

DROP POLICY IF EXISTS "Users can insert their own profile" ON user_profiles;
CREATE POLICY "Users can insert their own profile"
    ON user_profiles FOR INSERT
    TO authenticated
    WITH CHECK (auth.uid() = user_id);

SELECT 'Migration completed successfully' as result;`;

  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      console.log('🔧 Running database diagnostics...');

      const diagnostics = await Promise.allSettled([
        // Check if user_profiles table exists and what type
        supabase
          .from('user_profiles')
          .select('id')
          .limit(0)
          .then(({ error }) => ({
            name: 'user_profiles_table_access',
            exists: !error,
            error: error?.message
          })),

        // Check count of auth users
        supabase.auth.getUser().then(({ data, error }) => ({
          name: 'auth_check',
          currentUser: !!data.user,
          error: error?.message
        })),

        // Test if we can create a user profile manually (to test structure)
        supabase
          .from('user_profiles')
          .select('user_id, username, created_at')
          .limit(5)
          .then(({ data, error }) => ({
            name: 'user_profiles_sample_data',
            recordCount: data?.length || 0,
            sampleData: data?.slice(0, 2) || [],
            error: error?.message
          })),
      ]);

      const results = diagnostics.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          return {
            name: `diagnostic_${index}`,
            error: result.reason?.message || 'Unknown error'
          };
        }
      });

      setResults({
        success: true,
        diagnostics: results,
        message: 'Diagnostics completed',
        recommendation: generateRecommendation(results)
      });

    } catch (err: any) {
      console.error('Diagnostics failed:', err);
      setError(err.message || 'Failed to run diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendation = (diagnostics: any[]) => {
    const userProfilesAccess = diagnostics.find(d => d.name === 'user_profiles_table_access');
    
    if (!userProfilesAccess?.exists) {
      return 'CRITICAL: user_profiles table does not exist or is not accessible. Migration required.';
    }
    
    const sampleData = diagnostics.find(d => d.name === 'user_profiles_sample_data');
    if (sampleData?.error) {
      return `WARNING: user_profiles table exists but has access issues: ${sampleData.error}`;
    }
    
    return 'SUCCESS: user_profiles table appears to be working correctly.';
  };

  const showMigrationInstructions = () => {
    setResults({
      success: true,
      message: 'Migration Instructions',
      migrationSteps: [
        '1. Access your Supabase dashboard SQL editor',
        '2. Copy the definitive fix migration SQL (shown below)',
        '3. Execute the migration in the SQL editor',
        '4. Return here and run diagnostics to verify',
        '5. Test signup flow to confirm fix'
      ],
      migrationSql: definitiveFixMigration
    });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Migration Runner
        </CardTitle>
        <CardDescription>
          Execute SQL migrations and database fixes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={runDiagnostics} 
            disabled={loading}
            className="flex items-center gap-2"
          >
            <Database className="h-4 w-4" />
            {loading ? 'Running...' : 'Run Diagnostics'}
          </Button>
          
          <Button 
            onClick={showMigrationInstructions} 
            disabled={loading}
            variant="outline"
            className="flex items-center gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Show Migration Instructions
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="font-medium">Results:</span>
              {results.success ? (
                <Badge variant="default">Success</Badge>
              ) : (
                <Badge variant="destructive">Failed</Badge>
              )}
            </div>
            
            {results.message && (
              <p className="text-sm font-medium text-blue-700">{results.message}</p>
            )}
            
            {results.recommendation && (
              <div className={`p-3 rounded-md border ${
                results.recommendation.includes('CRITICAL') ? 'bg-red-50 border-red-200' :
                results.recommendation.includes('WARNING') ? 'bg-yellow-50 border-yellow-200' :
                'bg-green-50 border-green-200'
              }`}>
                <p className="text-sm font-medium">Recommendation:</p>
                <p className="text-sm">{results.recommendation}</p>
              </div>
            )}
            
            {results.diagnostics && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Diagnostic Results:</p>
                {results.diagnostics.map((diagnostic: any, index: number) => (
                  <div key={index} className="p-2 bg-gray-50 rounded border">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs">{diagnostic.name}</span>
                      {diagnostic.error ? (
                        <Badge variant="destructive" className="text-xs">Error</Badge>
                      ) : (
                        <Badge variant="default" className="text-xs">OK</Badge>
                      )}
                    </div>
                    {diagnostic.error && (
                      <p className="text-xs text-red-600 mt-1">{diagnostic.error}</p>
                    )}
                    {diagnostic.recordCount !== undefined && (
                      <p className="text-xs text-gray-600">Records: {diagnostic.recordCount}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {results.migrationSteps && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Migration Steps:</p>
                <ol className="text-sm space-y-1">
                  {results.migrationSteps.map((step: string, index: number) => (
                    <li key={index} className="text-gray-700">{step}</li>
                  ))}
                </ol>
              </div>
            )}
            
            {results.migrationSql && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Migration SQL:</p>
                <div className="relative">
                  <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded border overflow-auto max-h-64">
                    {results.migrationSql}
                  </pre>
                  <Button
                    size="sm"
                    variant="outline"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      navigator.clipboard.writeText(results.migrationSql);
                      // You could show a toast here
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground border-t pt-3">
          <p><strong>ℹ️ Info:</strong> This tool provides database diagnostics and migration guidance.</p>
          <p>Execute SQL migrations through the Supabase dashboard for safety.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MigrationRunner;